import { useQuery } from '@apollo/client';
import { type PropsWithChildren as PWC, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import { useAuthContext } from '@myphx-engineering/auth/client';
import { useTelemetryContext } from '@myphx-engineering/instrumentation/react';

import { Loader } from '../components/common/Loader';
import { routes } from '../components/router/routes';
import { getFragment } from '../graphql/client/helpers/get-fragment';
import { GetPatientDocument } from '../graphql/generated/graphql';
import { PatientContext } from '../hooks/use-patient-context';
import { getAbsoluteRoute } from '../util/get-absolute-route';

type PatientDataLoaderProps = { patientId: string };

const PatientDataLoader = ({
    children,
    patientId,
}: PWC<PatientDataLoaderProps>) => {
    const { data, loading, error } = useQuery(GetPatientDocument, {
        variables: { patientId },
    });

    if (error) {
        // oxlint-disable-next-line no-console
        console.error('Error loading patient data:', error);
        throw new Error('Forbidden');
    }

    if (loading) {
        return <Loader />;
    }

    if (!data?.patient) {
        return <Navigate to={getAbsoluteRoute('error')} />;
    }

    const patient = getFragment(data.patient);

    return (
        <PatientContext.Provider value={{ patient }}>
            {children}
        </PatientContext.Provider>
    );
};

export const PatientProvider = ({ children }: PWC) => {
    const { isAuthenticated, user } = useAuthContext();
    const { telemetryInstance } = useTelemetryContext();
    const { pathname } = useLocation();
    // Workaround: lilly-direct's AuthProvider lives inside AppRoot, so LoginCallback
    // cannot be moved outside the provider tree the way my-prescryptive does. Skip
    // PatientDataLoader on the callback route to prevent a stale-token refresh race
    // (MYPT-704). The structural fix is preferable if AuthProvider is ever lifted out.
    const isLoginCallback = pathname === `/${routes.loginCallback.root}`;

    useEffect(() => {
        if (isAuthenticated && user?.id) {
            telemetryInstance.setUserId(user.id);
        }
    }, [isAuthenticated, user?.id, telemetryInstance]);

    return isAuthenticated && !isLoginCallback ? (
        <PatientDataLoader patientId={user!.id}>{children}</PatientDataLoader>
    ) : (
        <>{children}</>
    );
};
